import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, MapPin, BadgeCheck, Crown, ListPlus, Rocket, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/store";
import ListingCard from "@/components/ListingCard";
import MapView from "@/components/MapView";
import AdBanner from "@/components/AdBanner";

const HERO = "https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxwZW9wbGUlMjBoYW5kaW5nJTIwb3ZlciUyMGJveHxlbnwwfHx8fDE3ODU1MzU3Njd8MA&ixlib=rb-4.1.0&q=85";
const CATS = ["all", "electronics", "outdoors", "music", "tools", "vehicles", "fashion"];

export default function Home() {
  const { t, lang, user } = useApp();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [country, setCountry] = useState("AU");
  const [advertiserType, setAdvertiserType] = useState("all");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [locQuery, setLocQuery] = useState("");
  const [radius, setRadius] = useState("50");
  const [geo, setGeo] = useState(null);
  const [city, setCity] = useState(null);
  const [view, setView] = useState("list");
  const [titles, setTitles] = useState({});
  const [showcase, setShowcase] = useState([]);
  useEffect(() => { api.get("/showcase", { params: { country } }).then(({ data }) => setShowcase(data)).catch(() => setShowcase([])); }, [country]);

  const translateAll = async () => {
    if (Object.keys(titles).length) { setTitles({}); return; }
    await translateTitles(lang);
  };

  const translateTitles = async (targetLang) => {
    if (targetLang === "en" || !items.length) { setTitles({}); return; }
    try {
      const { data } = await api.post("/translate/batch", { texts: items.map((i) => i.title), target_lang: targetLang });
      const map = {}; items.forEach((it, i) => { map[it.listing_id] = data.texts[i]; });
      setTitles(map);
    } catch { /* ignore */ }
  };

  // Persistent translation: auto-translate listings into the chosen language on every visit.
  useEffect(() => {
    if (lang !== "en" && items.length) translateTitles(lang);
    else if (lang === "en") setTitles({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, lang]);

  const load = async (g = geo) => {
    setLoading(true);
    try {
      const params = { q, category, country, advertiser_type: advertiserType, verified_only: verifiedOnly };
      if (g) { params.lat = g.lat; params.lng = g.lng; params.radius_km = radius; }
      const { data } = await api.get("/listings", { params });
      setItems(data);
    } finally { setLoading(false); }
  };

  const searchNear = async () => {
    if (!locQuery.trim()) return;
    try {
      const c = country !== "all" ? country.toLowerCase() : undefined;
      const { data } = await api.get("/geocode", { params: { query: locQuery, country: c } });
      const g = { lat: data.lat, lng: data.lng, label: data.display_name || locQuery };
      setGeo(g);
      toast.success(`Showing items near ${g.label}`);
      load(g);
    } catch { toast.error("Location not found. Try a city name or postal code."); }
  };
  const clearNear = () => { setGeo(null); setLocQuery(""); setCity(null); load(null); };
  const CITIES = [["Sydney", -33.8688, 151.2093], ["Melbourne", -37.8136, 144.9631], ["Brisbane", -27.4698, 153.0251]];
  const pickCity = (name, lat, lng) => {
    if (city === name) { clearNear(); return; }
    const g = { lat, lng, label: name };
    setCity(name); setGeo(g); setLocQuery(name); if (country !== "AU") setCountry("AU");
    load(g);
  };
  const followCategory = async () => {
    if (!user) { navigate("/login"); return; }
    try { await api.post("/subscriptions", { category, country: country !== "all" ? country : null }); toast.success(`You'll be alerted about new ${category} items`); }
    catch { toast.error("Could not follow category"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, country, advertiserType, verifiedOnly]);

  return (
    <div>
      <section className="relative overflow-hidden border-b border-gray-200 bg-[#002FA7]">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="max-w-2xl rise">
            <div className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-white/70">Global P2P Rental Marketplace</div>
            <h1 className="font-display text-4xl font-extrabold leading-none tracking-tight text-white sm:text-5xl lg:text-6xl">{t("hero_title")}</h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/80">{t("hero_subtitle")}</p>
            <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mt-8 flex max-w-lg items-center gap-2 rounded-full bg-white p-1.5 shadow-xl">
              <Search className="ml-3 text-gray-400" size={20} />
              <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={t("search_placeholder")}
                className="w-full bg-transparent px-2 py-2 text-gray-800 outline-none" />
              <button data-testid="search-btn" type="submit" className="rounded-full bg-[#002FA7] px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#002280]">{t("hero_cta")}</button>
            </form>
          </div>
        </div>
      </section>

      <section className="border-b border-gray-200 bg-gray-50" data-testid="home-how-it-works">
        <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
          <h2 className="text-base font-bold uppercase tracking-[0.15em] text-gray-400 md:text-lg">{t("hiw_home_title")}</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-3">
            {[["hiw1", ListPlus], ["hiw2", Rocket], ["hiw3", MessageSquare]].map(([k, Icon], i) => (
              <div key={k} className="flex gap-4" data-testid={`home-step-${i + 1}`}>
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#002FA7]/10 text-[#002FA7]"><Icon size={20} /></span>
                <div>
                  <h3 className="font-display text-base font-bold text-gray-900">{i + 1}. {t(`${k}_t`)}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-gray-500">{t(`${k}_d`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            {CATS.map((c) => (
              <button key={c} data-testid={`cat-${c}`} onClick={() => setCategory(c)}
                className={`rounded-full border px-4 py-1.5 text-sm font-semibold capitalize transition-colors ${category === c ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-gray-200 text-gray-600 hover:border-[#002FA7]"}`}>
                {c === "all" ? t("filter_all") : c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-gray-500">
            <div className="mr-1 flex items-center rounded-full border border-gray-200 p-0.5" data-testid="view-toggle">
              <button data-testid="view-list" onClick={() => setView("list")} className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${view === "list" ? "bg-[#002FA7] text-white" : "text-gray-500"}`}>{t("view_list")}</button>
              <button data-testid="view-map" onClick={() => setView("map")} className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${view === "map" ? "bg-[#002FA7] text-white" : "text-gray-500"}`}>{t("view_map")}</button>
            </div>
            <button data-testid="translate-all" onClick={translateAll} className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]">🌐 {Object.keys(titles).length ? "Original" : "Translate"}</button>
            <SlidersHorizontal size={16} />
            <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="country-filter"
              className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold outline-none focus:ring-2 focus:ring-[#002FA7]">
              <option value="all">🌍 All countries</option>
              <option value="BR">🇧🇷 Brazil</option>
              <option value="AU">🇦🇺 Australia</option>
              <option value="US">🇺🇸 United States</option>
            </select>
            <select value={advertiserType} onChange={(e) => setAdvertiserType(e.target.value)} data-testid="advertiser-filter"
              className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold outline-none focus:ring-2 focus:ring-[#002FA7]">
              <option value="all">👥 All advertisers</option>
              <option value="business">🏢 Businesses</option>
              <option value="individual">🙋 Individuals</option>
            </select>
            <label data-testid="verified-only-filter" className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${verifiedOnly ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-gray-200 bg-white text-gray-600 hover:border-[#002FA7]"}`}>
              <input type="checkbox" data-testid="verified-only-checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} className="hidden" />
              <BadgeCheck size={14} /> {t("verified_only")}
            </label>
          </div>
        </div>

        <div className="mb-8 flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4" data-testid="location-search">
          <div className="flex w-full flex-wrap items-center gap-2" data-testid="city-shortcuts">
            <span className="text-xs font-bold uppercase tracking-widest text-gray-400">{t("featured_cities")}</span>
            {CITIES.map(([name, lat, lng]) => (
              <button key={name} data-testid={`city-${name.toLowerCase()}`} onClick={() => pickCity(name, lat, lng)}
                className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${city === name ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-gray-200 bg-white text-gray-600 hover:border-[#002FA7]"}`}>
                <MapPin size={12} className="mr-1 inline" />{name}
              </button>
            ))}
          </div>
          <div className="min-w-[200px] flex-1">
            <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-gray-400"><MapPin size={12} className="mr-1 inline" />{t("near_me")}</label>
            <input data-testid="location-input" value={locQuery} onChange={(e) => setLocQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") searchNear(); }} placeholder={t("search_near")}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none transition-colors focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-gray-400">{t("radius")}</label>
            <select data-testid="radius-select" value={radius} onChange={(e) => setRadius(e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 font-semibold outline-none focus:ring-2 focus:ring-[#002FA7]">
              <option value="5">5 km</option><option value="10">10 km</option><option value="50">50 km</option><option value="200">200 km</option>
            </select>
          </div>
          <button data-testid="location-search-btn" onClick={searchNear} className="rounded-full bg-[#002FA7] px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#002280]">Search</button>
          {geo && <button data-testid="clear-location" onClick={clearNear} className="rounded-full border border-gray-300 px-4 py-2.5 text-sm font-bold text-gray-600 transition-colors hover:bg-gray-100">Clear location</button>}
        </div>

        <div className="mb-6"><AdBanner /></div>

        {showcase.length > 0 && view === "list" && (
          <section className="mb-10 rounded-3xl bg-[#0A0B10] p-6 sm:p-8" data-testid="showcase-strip">
            <div className="mb-5 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-yellow-300"><Crown size={16} /> {t("showcase_title")}</div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {showcase.map((it, i) => <ListingCard key={`sc-${it.listing_id}`} item={it} index={i} titleOverride={titles[it.listing_id]} />)}
            </div>
          </section>
        )}

        {loading ? (
          <div className="py-24 text-center text-gray-400" data-testid="listings-loading">Loading listings…</div>
        ) : view === "map" ? (
          <MapView items={items} center={geo ? [geo.lat, geo.lng] : null} />
        ) : items.length === 0 ? (
          <div className="py-24 text-center text-gray-400" data-testid="listings-empty">No listings found.</div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="listings-grid">
            {items.map((it, i) => <ListingCard key={it.listing_id} item={it} index={i} titleOverride={titles[it.listing_id]} />)}
          </div>
        )}
      </section>
    </div>
  );
}
